import {
    DataSource,
    DerivedComponent,
    FormComponentProps,
    FormType,
    KeyGridMeta,
    SelectComponent
} from "../../DynamicFormTypes.ts";
import React, {useEffect, useMemo} from "react";
import {useWatch} from "react-hook-form";
import {useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {Select} from "../Select.tsx";
import {extractDataGridKey} from "../../dyanimcFormUtil.tsx";
import {findKey, getStringValue} from "./derivedUtils.ts";
import {useThrottled} from "../../hooks/useThrottled.ts";
import {isNull} from "lodash";
import {Spacer} from "../Spacer.tsx";

const SometimesSelectRenderer: React.FC<FormComponentProps<DerivedComponent>> = ({component}) => {
    const {formMethods} = useFormMethods();
    const { flattenedSchemaMap } = useFormStatic();
    const {control, getValues} = formMethods;

    const watchKeys = useMemo(() => [
        ...new Set(component.listFieldKeys.filter(f => f.dataGrid).map(f => f.dataGrid!)),
        ...component.listFieldKeys.filter(f => !f.dataGrid).map(f => f.fieldKey),
    ], [component.listFieldKeys]);

    const forcedSelectOption = useMemo(() => {
        if (component.forcedValue === null) {
            return null;
        }
        const watchedField = findKey(KeyGridMeta.WATCHED_SELECT_FIELD, component);
        const watchedComponent = flattenedSchemaMap.get(watchedField!) as SelectComponent | DerivedComponent;
        let option =  watchedComponent?.provided?.find(option =>
            option.value === component.forcedValue
        ) ?? { label: component.forcedValue, value: component.forcedValue };
        return JSON.stringify(option);
    }, [component.forcedValue, watchKeys]);


    const trigger = useWatch({control, name: watchKeys});
    const throttledTrigger = useThrottled(trigger, 150);

    const conditionHasBeenMet = useMemo(() => {
        const fullKey = extractDataGridKey(component.fieldKey);
        const watchedValue = getStringValue(fullKey, findKey(KeyGridMeta.WATCHED_SELECT_FIELD, component), getValues);
        if (!watchedValue) {
            return false;
        }

        return component.forceValueWhen?.includes(watchedValue);
    }, [throttledTrigger]);

    useEffect(() => {
        if (conditionHasBeenMet) {
            formMethods.setValue(component.fieldKey, forcedSelectOption, {shouldValidate:true});
        }
    }, [conditionHasBeenMet]);

    if (conditionHasBeenMet && isNull(component.forcedValue)) {
        return <Spacer component={{type: FormType.SPACER, size: 1, fieldKey: component.fieldKey, label: component.label, compactUI: component.compactUI}}/>
    }

    return <Select component={{type: FormType.SELECT, fieldKey: component.fieldKey, label: component.label, compactUI: component.compactUI, dataSource: DataSource.PROVIDED, provided: component.provided, validationRules: component.validationRules, suffix: component.suffix, defaultValue: component.defaultValue, fieldsToTriggerOnBlur: component.fieldsToTriggerOnBlur}} disabled={conditionHasBeenMet}></Select>;

};

export const SometimesSelect = React.memo(SometimesSelectRenderer);